<template>
  <!-- Group by -->
  <div class="col-xs-6 col-sm-4">
    <q-select
      filled
      v-model="grouping"
      :options="groupingOptions"
      color="accent"
      class="ellipsis"
      @update:model-value="groupingChange"
      :label="t('data.filtering.groupBy')"
      ref="groupingRef"
      behavior="menu"
    >
      <template #selected-item>
        <span class="ellipsis">{{ grouping?.label }}</span></template
      >
      <template v-if="grouping" #append>
        <q-icon name="cancel" @click.stop="cancelGrouping" class="cursor-pointer" />
      </template>
    </q-select>
  </div>
  <!-- Sort by -->
  <div class="col-xs-6 col-sm-4">
    <q-select
      filled
      v-model="sorting"
      @update:model-value="sortingChange"
      :options="groupingOptions"
      class="ellipsis"
      color="accent"
      :label="t('data.filtering.sortBy')"
      ref="sortingRef"
      behavior="menu"
    >
      <template #selected-item>
        <span class="ellipsis">{{ sorting?.label }}</span></template
      >
      <template #append>
        <!-- Sort Direction -->
        <q-icon v-if="sorting != null" :class="sortArrow" @click.stop="toggleSort"></q-icon>
        <!-- Cancel Sort -->
        <q-icon v-if="sorting" name="cancel" @click.stop="cancelSorting" class="cursor-pointer" />
      </template>
    </q-select>
  </div>
  <!-- Search -->
  <div class="col-xs-12 col-sm-4">
    <q-input
      filled
      :label="t('data.filtering.search')"
      v-model="filter"
      @update:model-value="filterChange"
      debounce="200"
      ref="searchRef"
      color="accent"
    >
      <template #append>
        <q-icon
          v-if="filter"
          name="close"
          @click.stop.prevent="cancelFilter"
          class="cursor-pointer"
        />
        <q-icon name="search" />
      </template>
    </q-input>
  </div>
</template>
<script lang="ts">
import { defineComponent, computed, ref, Ref, PropType, watch, toRef, onBeforeMount } from 'vue'
import { SortingKeyType } from 'app/composables/sortAlgorithm'
import { useI18n } from 'vue-i18n'
import { LanguageChoice } from '../i18n'
import { QInput, QSelect } from 'quasar'
import { ColumnMeta, ColumnsInterface } from '../types/components/data-rendering'

interface SelectionItem {
  name: string
  label: string
}
type SortingInterface = {
  name: string
  label: string
  type: SortingKeyType
}
export default defineComponent({
  emits: ['groupingChange', 'sortingChange', 'filterChange', 'toggleSort', 'toggleConditions'],
  props: {
    options: {
      required: true,
      type: Object as PropType<ColumnsInterface>,
    },
    selections: {
      required: false,
      type: Object,
      default: null,
    },
    selectedSortedValue: {
      required: false,
      type: String,
      default: null,
    },
    ascendingValue: {
      required: false,
      type: Boolean,
      default: null,
    },
  },

  setup(props, { emit }) {
    const { locale, t } = useI18n({ useScope: 'global' })
    const localeString = computed(() => locale.value as LanguageChoice)
    const filter: Ref<string | null> = ref(null)
    const grouping: Ref<SortingInterface | null> = ref(null)
    const groupingOptions = computed(() => {
      const out: SortingInterface[] = []
      for (const name in props.options) {
        const val: ColumnMeta = props.options[name]
        if (!val.hideInFilters) {
          out.push({ name: name, label: val.label[localeString.value], type: val.type })
        }
      }
      return out
    })
    const sorting: Ref<SortingInterface | null> = ref(null)
    const selectedSortedValue = toRef(props, 'selectedSortedValue')
    watch([selectedSortedValue, localeString], (): void => {
      if (selectedSortedValue.value != null) {
        sorting.value = {
          name: selectedSortedValue.value,
          label: props.options[selectedSortedValue.value].label[localeString.value],
          type: props.options[selectedSortedValue.value].type,
        }
      } else {
        sorting.value = null
      }
    })

    const groupingRef = ref<QSelect | null>(null)
    const sortingRef = ref<QSelect | null>(null)
    const searchRef = ref<QInput | null>(null)

    const sortingChange = (event: any): void => {
      emit('sortingChange', event.name)
    }
    const cancelSorting = async (): Promise<void> => {
      sorting.value = null
      await sortingRef.value?.hidePopup()
      sortingRef.value?.blur()
      emit('sortingChange', null)
    }
    const filterChange = (event: any): void => {
      emit('filterChange', event)
    }
    const cancelFilter = (): void => {
      filter.value = null
      searchRef.value?.blur()
      emit('filterChange', null)
    }
    const groupingChange = (event: any): void => {
      emit('groupingChange', event.name)
    }
    const cancelGrouping = async (): Promise<void> => {
      grouping.value = null
      await groupingRef.value?.hidePopup()
      groupingRef.value?.blur()
      emit('groupingChange', null)
    }

    const selections = toRef(props, 'selections')
    onBeforeMount(() => {
      if (selections.value) {
        if (typeof selections.value.sortBy !== undefined) {
          sorting.value = groupingOptions.value.find(
            (el: SelectionItem) => el.name == selections.value.sortBy
          )!
          sortingChange(sorting.value)
        }
        if (typeof selections.value.groupBy !== undefined) {
          grouping.value = groupingOptions.value.find(
            (el: SelectionItem) => el.name == selections.value.groupBy
          )!
        }
      }
    })
    // watch([selections, localeString], (): void => {
    //   if (selections.value) {
    //     if (typeof selections.value.sortBy !== undefined) {
    //       sorting.value = groupingOptions.value.find(
    //         (el: SelectionItem) => el.name == selections.value.sortBy
    //       )!
    //     }
    //     if (typeof selections.value.groupBy !== undefined) {
    //       grouping.value = groupingOptions.value.find(
    //         (el: SelectionItem) => el.name == selections.value.groupBy
    //       )!
    //     }
    //   }
    // })

    const toggleSort = (): void => {
      emit('toggleSort')
    }
    const sortArrow = computed((): string => {
      if (props.ascendingValue === null) {
        return ''
      }
      const alphaOptions: SortingKeyType[] = ['string', 'date', 'boolean']
      const numericOptions: SortingKeyType[] = ['number', 'time']
      let arrowType: 'alpha' | 'numeric' | null = null
      if (sorting.value == null || sorting.value.type == null) {
        return ''
      }
      if (alphaOptions.includes(sorting.value.type)) {
        arrowType = 'alpha'
      } else if (numericOptions.includes(sorting.value.type)) {
        arrowType = 'numeric'
      }
      //falsiness determines up/down
      if (props.ascendingValue) {
        return `las la-sort-${arrowType}-down`
      } else {
        return `las la-sort-${arrowType}-up-alt`
      }
    })
    return {
      groupingRef,
      sortingRef,
      searchRef,
      cancelFilter,
      cancelGrouping,
      cancelSorting,
      toggleSort,
      sortArrow,
      filter,
      groupingOptions,
      groupingChange,
      sortingChange,
      filterChange,
      sorting,
      grouping,
      t,
    }
  },
})
</script>
